import { ApolloError } from '@apollo/client';
import { clearCustomerListCollection } from '@finance-hub/domain/customers/blocs/collections/clear-customer-list-collection';
import { customerListCollection } from '@finance-hub/domain/customers/blocs/collections/customer-list-collection';
import { FinanceHubCustomerRow } from '@finance-hub/domain/customers/blocs/types/customer-list';
import { insertCustomerListRows } from '@finance-hub/domain/customers/blocs/collections/insert-customer-list-rows';
import { transformFinanceHubCustomerListItem } from '@finance-hub/domain/customers/blocs/transformers/customer-list-transformer';
import { useEffect } from 'react';
import { useGeneratedFinanceHubCustomerListQuery } from '@finance-hub/domain/customers/blocs/documents/customer-list.generated';
import { useLiveQuery } from '@platform/db/tanstack-db';

const INITIAL_LIMIT = 50;

interface UseCustomerListLoaderResult {
  error: ApolloError | undefined;
  fullListError: ApolloError | undefined;
  fullListLoading: boolean;
  loading: boolean;
  refetch: () => void;
  rows: FinanceHubCustomerRow[];
}

/**
 * Loads the Customers table using two parallel requests: a `limit: 50` request
 * for a fast first render, and an unbounded request for the rest. Both write
 * into the customer list collection, and the table reads its rows back from
 * there rather than joining the two responses.
 *
 * @returns Rows plus the loading/error state of both requests.
 */
export const useCustomerListLoader = (): UseCustomerListLoaderResult => {
  const initialQuery = useGeneratedFinanceHubCustomerListQuery({
    context: { noBatch: true },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: { limit: INITIAL_LIMIT },
  });
  const completeQuery = useGeneratedFinanceHubCustomerListQuery({
    context: { noBatch: true },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {},
  });

  const completeList = completeQuery.data?.getFinanceHubCustomerList;
  const initialList = initialQuery.data?.getFinanceHubCustomerList;

  // The collection outlives this hook, so drop its rows on unmount rather than
  // letting them survive into the next visit.
  useEffect(() => clearCustomerListCollection, []);

  useEffect(() => {
    if (initialList) {
      insertCustomerListRows(
        initialList.customers.map(transformFinanceHubCustomerListItem),
      );
    }
  }, [initialList]);

  useEffect(() => {
    if (completeList) {
      insertCustomerListRows(
        completeList.customers.map(transformFinanceHubCustomerListItem),
      );
    }
  }, [completeList]);

  const { data: rows } = useLiveQuery((q) =>
    q.from({ customerListCollection }),
  );

  return {
    // Page-level error only once both requests fail; a failed 50-row request
    // still recovers through the complete one.
    error:
      initialQuery.error && completeQuery.error
        ? completeQuery.error
        : undefined,
    // Stale `error` survives while a refetch is in flight; the banner should
    // yield to the loading footer until that retry settles.
    fullListError:
      !completeList && initialList && !completeQuery.loading
        ? completeQuery.error
        : undefined,
    fullListLoading: !completeList && !!initialList && completeQuery.loading,
    loading:
      !initialList &&
      !completeList &&
      (initialQuery.loading || completeQuery.loading),
    refetch: () => void completeQuery.refetch(),
    rows,
  };
};
